import React, { useState, useEffect, useCallback } from "react";
import api from "@/services/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Package,
  Search,
  RefreshCw,
  Eye,
  CheckCircle,
  Clock,
  Truck,
  MapPin,
  ChefHat,
  PackageCheck,
  XCircle,
  Phone,
  User,
  CreditCard,
  Banknote,
  Building2,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  DollarSign,
  Link2,
  Copy,
  Check,
  ExternalLink,
  Calendar,
  Edit,
  Trash2,
  Plus,
  Minus,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const ESTADOS_EDITABLES_ITEMS = ["recibido", "pendiente_confirmacion", "pago_pendiente", "pago_confirmado"];

const estadoConfig = {
  recibido: { icon: Package, label: "Recibido", color: "bg-blue-500", textColor: "text-blue-600" },
  pago_pendiente: { icon: CreditCard, label: "Pago Pendiente", color: "bg-yellow-500", textColor: "text-yellow-600" },
  pago_confirmado: { icon: CheckCircle, label: "Pago Confirmado", color: "bg-green-500", textColor: "text-green-600" },
  ya_pagado: { icon: CheckCircle, label: "Ya Pagado", color: "bg-emerald-500", textColor: "text-emerald-600" },
  confirmado: { icon: CheckCircle, label: "Confirmado", color: "bg-green-500", textColor: "text-green-600" },
  en_produccion: { icon: ChefHat, label: "En Producción", color: "bg-orange-500", textColor: "text-orange-600" },
  listo: { icon: PackageCheck, label: "Listo", color: "bg-purple-500", textColor: "text-purple-600" },
  en_ruta: { icon: Truck, label: "En Ruta", color: "bg-blue-600", textColor: "text-blue-700" },
  entregado: { icon: MapPin, label: "Entregado", color: "bg-green-600", textColor: "text-green-700" },
  cancelado: { icon: XCircle, label: "Cancelado", color: "bg-red-500", textColor: "text-red-600" },
};

const metodoPagoConfig = {
  efectivo_vendedor: { icon: Banknote, label: "Efectivo", color: "text-green-600", desc: "Cobra en efectivo" },
  transferencia_vendedor: { icon: Phone, label: "Transfer. a Ti", color: "text-blue-600", desc: "Te transfieren a ti" },
  transferencia_baruj: { icon: Building2, label: "Transfer. Baruj", color: "text-purple-600", desc: "Pagan a la empresa" },
  contra_entrega: { icon: Truck, label: "Contra Entrega", color: "text-orange-600", desc: "Cobras al entregar" },
};

export default function VendedorCatalogOrders() {
  const { user } = useAuth();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [confirmarPagoDialog, setConfirmarPagoDialog] = useState({ open: false, pedido: null });
  const [catalogLinkCopied, setCatalogLinkCopied] = useState(false);

  // Edición de artículos (pedidos propios no confirmados)
  const [editItemsDialog, setEditItemsDialog] = useState({ open: false, pedido: null });
  const [itemsEditados, setItemsEditados] = useState([]);
  const [motivoCambio, setMotivoCambio] = useState("");
  const [guardandoItems, setGuardandoItems] = useState(false);
  const [productosDisponibles, setProductosDisponibles] = useState([]);

  const catalogUrl = user?.catalog_code 
    ? `${window.location.origin}/catalogo/${user.catalog_code}`
    : null;

  const fetchPedidos = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/catalogo/vendor/orders";
      if (filtroEstado) url += `?estado=${filtroEstado}`;
      
      const res = await api.get(url);
      setPedidos(res.data.pedidos || []);
    } catch (error) {
      console.error("Error fetching pedidos:", error);
      toast.error("Error al cargar pedidos");
    } finally {
      setLoading(false);
    }
  }, [filtroEstado]);

  useEffect(() => {
    fetchPedidos();
  }, [fetchPedidos]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatDeliveryDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr + "T12:00:00");
    return date.toLocaleDateString("es-CO", {
      weekday: "short",
      day: "numeric",
      month: "short"
    });
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  // ===== Edición de artículos (mismo historial que admin) =====
  const abrirEdicionItems = async (pedido) => {
    setItemsEditados((pedido.items || []).map((i) => ({ ...i })));
    setMotivoCambio("");
    setEditItemsDialog({ open: true, pedido });
    if (productosDisponibles.length === 0) {
      try {
        const res = await api.get("/productos");
        setProductosDisponibles(res.data.filter((p) => p.activo));
      } catch (err) {
        console.error("Error cargando productos:", err);
      }
    }
  };

  const cambiarCantidad = (idx, delta) => {
    setItemsEditados((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, cantidad: Math.max(1, it.cantidad + delta) } : it))
    );
  };

  const agregarProductoEdit = (productoId) => {
    const producto = productosDisponibles.find((p) => p.id === productoId);
    if (!producto) return;
    setItemsEditados((prev) => {
      const idx = prev.findIndex((i) => i.producto_id === producto.id);
      if (idx >= 0) return prev.map((it, i) => (i === idx ? { ...it, cantidad: it.cantidad + 1 } : it));
      return [...prev, { producto_id: producto.id, producto_nombre: producto.nombre, cantidad: 1, precio_unitario: producto.precio, subtotal: producto.precio }];
    });
  };

  const guardarEdicionItems = async () => {
    if (itemsEditados.length === 0) {
      toast.error("El pedido debe tener al menos un artículo");
      return;
    }
    setGuardandoItems(true);
    try {
      await api.patch(`/catalogo/vendor/orders/${editItemsDialog.pedido.id}/edit-items`, {
        items: itemsEditados.map((i) => ({
          producto_id: i.producto_id,
          producto_nombre: i.producto_nombre,
          cantidad: i.cantidad,
          precio_unitario: i.precio_unitario,
        })),
        motivo: motivoCambio || "Modificación por el vendedor",
      });
      toast.success("Artículos del pedido actualizados");
      setEditItemsDialog({ open: false, pedido: null });
      fetchPedidos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al modificar los artículos");
    } finally {
      setGuardandoItems(false);
    }
  };

  // Confirmar pago (solo para efectivo_vendedor o transferencia_vendedor)
  const handleConfirmarPago = async () => {
    try {
      await api.post(`/catalogo/vendor/orders/${confirmarPagoDialog.pedido.id}/confirm-payment`);
      toast.success("Pago confirmado - se sumará a tu cierre de caja");
      setConfirmarPagoDialog({ open: false, pedido: null });
      fetchPedidos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al confirmar pago");
    }
  };

  const copyCatalogLink = () => {
    if (catalogUrl) {
      navigator.clipboard.writeText(catalogUrl);
      setCatalogLinkCopied(true);
      toast.success("Link copiado");
      setTimeout(() => setCatalogLinkCopied(false), 2000);
    }
  };

  // Filtrar por búsqueda
  const pedidosFiltrados = pedidos.filter(p => {
    if (!busqueda) return true;
    const search = busqueda.toLowerCase();
    return (
      p.numero_pedido?.toLowerCase().includes(search) ||
      p.cliente_nombre?.toLowerCase().includes(search) ||
      p.cliente_telefono?.includes(search)
    );
  });

  // Stats
  const stats = {
    total: pedidos.length,
    pendientes: pedidos.filter(p => ["recibido", "pago_pendiente"].includes(p.estado)).length,
    porCobrar: pedidos.filter(p => 
      ["efectivo_vendedor", "transferencia_vendedor"].includes(p.metodo_pago) && 
      p.estado_pago !== "confirmado_vendedor"
    ).length,
    totalVentas: pedidos.reduce((sum, p) => sum + (p.total || 0), 0)
  };

  return (
    <div className="space-y-6" data-testid="vendedor-catalog-orders">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Mis Pedidos del Catálogo</h1>
          <p className="text-torta-muted">Pedidos recibidos a través de tu catálogo digital</p>
        </div>
        <Button variant="outline" onClick={fetchPedidos} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      {/* Widget Link del Catálogo */}
      {catalogUrl && (
        <div className="bg-gradient-to-r from-[#4A2C0A] to-[#6D4C1A] rounded-2xl p-4 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center">
                <Link2 className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium">Tu Catálogo Digital</p>
                <p className="text-white/70 text-sm truncate max-w-xs">{catalogUrl}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={copyCatalogLink}
                className="bg-white text-[#4A2C0A] hover:bg-white/90 h-9 rounded-xl"
              >
                {catalogLinkCopied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                Copiar
              </Button>
              <Button
                onClick={() => window.open(catalogUrl, "_blank")}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 h-9 rounded-xl"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Abrir
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-torta-sand/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-torta-muted text-sm">Total Pedidos</p>
              <p className="font-display text-2xl text-torta-dark">{stats.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-torta-sand/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-torta-muted text-sm">Pendientes</p>
              <p className="font-display text-2xl text-torta-dark">{stats.pendientes}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-torta-sand/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <Banknote className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-torta-muted text-sm">Por Cobrar</p>
              <p className="font-display text-2xl text-torta-dark">{stats.porCobrar}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-torta-sand/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-torta-muted text-sm">Total Ventas</p>
              <p className="font-display text-lg text-torta-dark">{formatCurrency(stats.totalVentas)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl shadow-sm border border-torta-sand/50 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-torta-muted" />
            <Input
              placeholder="Buscar por número, cliente..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        {/* Pills de estado */}
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            onClick={() => setFiltroEstado("")}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              !filtroEstado ? "bg-torta-brown text-white" : "bg-torta-cream text-torta-brown"
            }`}
          >
            Todos
          </button>
          {["recibido", "confirmado", "en_produccion", "listo", "entregado"].map((key) => {
            const cfg = estadoConfig[key];
            return (
              <button
                key={key}
                onClick={() => setFiltroEstado(key)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                  filtroEstado === key ? `${cfg.color} text-white` : `bg-gray-100 ${cfg.textColor}`
                }`}
              >
                <cfg.icon className="w-3 h-3" />
                {cfg.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Lista de pedidos */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-2xl p-8 text-center">
            <RefreshCw className="w-8 h-8 text-torta-brown animate-spin mx-auto mb-2" />
            <p className="text-torta-muted">Cargando pedidos...</p>
          </div>
        ) : pedidosFiltrados.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center">
            <Package className="w-12 h-12 text-torta-sand mx-auto mb-3" />
            <p className="text-torta-muted">No tienes pedidos del catálogo</p>
            <p className="text-torta-muted text-sm mt-1">Comparte tu link para empezar a recibir pedidos</p>
          </div>
        ) : (
          pedidosFiltrados.map((pedido) => {
            const estadoCfg = estadoConfig[pedido.estado] || estadoConfig.recibido;
            const metodoCfg = metodoPagoConfig[pedido.metodo_pago] || metodoPagoConfig.efectivo_vendedor;
            const isExpanded = expandedRows.has(pedido.id);
            const canConfirmPayment = 
              ["efectivo_vendedor", "transferencia_vendedor"].includes(pedido.metodo_pago) &&
              pedido.estado_pago !== "confirmado_vendedor";
            
            return (
              <div key={pedido.id} className="bg-white rounded-2xl shadow-sm border border-torta-sand/50 overflow-hidden">
                {/* Header del pedido */}
                <div 
                  className="p-4 cursor-pointer hover:bg-torta-cream/30 transition-colors"
                  onClick={() => toggleRow(pedido.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${estadoCfg.color}`}>
                        <estadoCfg.icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-display text-lg text-torta-brown">{pedido.numero_pedido}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full text-white ${estadoCfg.color}`}>
                            {estadoCfg.label}
                          </span>
                        </div>
                        <p className="text-sm text-torta-muted">{pedido.cliente_nombre}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-xl text-torta-dark">{formatCurrency(pedido.total)}</p>
                      <div className={`flex items-center gap-1 text-sm ${metodoCfg.color}`}>
                        <metodoCfg.icon className="w-3 h-3" />
                        {metodoCfg.label}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-torta-sand/30">
                    <div className="flex items-center gap-4 text-sm text-torta-muted">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDeliveryDate(pedido.fecha_entrega)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {pedido.cliente_telefono}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {canConfirmPayment && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmarPagoDialog({ open: true, pedido });
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white h-8"
                        >
                          <Banknote className="w-3 h-3 mr-1" />
                          Confirmar Cobro
                        </Button>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-torta-muted" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-torta-muted" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Detalles expandidos */}
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-4 border-t border-torta-sand/30 bg-torta-cream/20">
                    <div className="grid sm:grid-cols-3 gap-4 pt-4">
                      {/* Productos */}
                      <div className="bg-white rounded-xl p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-torta-dark text-sm flex items-center gap-1">
                            <Package className="w-4 h-4 text-torta-brown" /> Productos
                          </h4>
                          {ESTADOS_EDITABLES_ITEMS.includes(pedido.estado) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                abrirEdicionItems(pedido);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              data-testid={`vendor-edit-items-btn-${pedido.numero_pedido}`}
                            >
                              <Edit className="w-3 h-3" /> Editar
                            </button>
                          )}
                        </div>
                        <div className="space-y-1">
                          {pedido.items?.map((item, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span>{item.cantidad}x {item.producto_nombre}</span>
                              <span className="text-torta-brown">{formatCurrency(item.subtotal)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Entrega */}
                      <div className="bg-white rounded-xl p-3">
                        <h4 className="font-medium text-torta-dark text-sm mb-2 flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-torta-brown" /> Entrega
                        </h4>
                        <div className="text-sm space-y-1">
                          <p>{pedido.tipo_entrega === "domicilio" ? "A domicilio" : "Recoge"}</p>
                          {pedido.direccion_entrega && <p className="text-torta-muted">{pedido.direccion_entrega}</p>}
                          {pedido.barrio_entrega && <p className="text-torta-muted">{pedido.barrio_entrega}</p>}
                        </div>
                      </div>
                      
                      {/* Contacto */}
                      <div className="bg-white rounded-xl p-3">
                        <h4 className="font-medium text-torta-dark text-sm mb-2 flex items-center gap-1">
                          <User className="w-4 h-4 text-torta-brown" /> Contactar
                        </h4>
                        <div className="space-y-2">
                          <a 
                            href={`https://wa.me/57${pedido.cliente_telefono?.replace(/\D/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-green-600 hover:text-green-700 text-sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MessageCircle className="w-4 h-4" />
                            WhatsApp
                          </a>
                          <a 
                            href={`tel:${pedido.cliente_telefono}`}
                            className="flex items-center gap-2 text-blue-600 text-sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Phone className="w-4 h-4" />
                            Llamar
                          </a>
                        </div>
                      </div>
                    </div>
                    
                    {pedido.notas && (
                      <div className="bg-yellow-50 p-3 rounded-xl">
                        <p className="text-sm text-yellow-800"><strong>Nota del cliente:</strong> {pedido.notas}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Dialog: Confirmar pago */}
      <Dialog open={confirmarPagoDialog.open} onOpenChange={(open) => setConfirmarPagoDialog({ open, pedido: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-600" />
              Confirmar Cobro
            </DialogTitle>
          </DialogHeader>
          {confirmarPagoDialog.pedido && (
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-xl">
                <p className="font-medium text-green-800">Pedido: {confirmarPagoDialog.pedido.numero_pedido}</p>
                <p className="text-green-600 text-2xl font-display mt-1">
                  {formatCurrency(confirmarPagoDialog.pedido.total)}
                </p>
                <p className="text-green-600 text-sm mt-1">
                  {metodoPagoConfig[confirmarPagoDialog.pedido.metodo_pago]?.desc}
                </p>
              </div>
              
              <p className="text-sm text-torta-muted">
                Al confirmar, este monto se sumará a tu cierre de caja del día.
              </p>
              
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setConfirmarPagoDialog({ open: false, pedido: null })}>
                  Cancelar
                </Button>
                <Button onClick={handleConfirmarPago} className="bg-green-600 hover:bg-green-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmar Cobro
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog editar artículos */}
      <Dialog open={editItemsDialog.open} onOpenChange={(o) => !o && setEditItemsDialog({ open: false, pedido: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Artículos — {editItemsDialog.pedido?.numero_pedido}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="vendor-edit-items-panel">
            {itemsEditados.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-torta-cream/40 p-2 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{item.producto_nombre}</p>
                  <p className="text-xs text-torta-muted">{formatCurrency(item.precio_unitario)} c/u</p>
                </div>
                <button onClick={() => cambiarCantidad(idx, -1)} className="w-7 h-7 rounded-full border flex items-center justify-center hover:bg-gray-100">
                  <Minus className="w-3 h-3" />
                </button>
                <span className="w-8 text-center font-semibold text-sm">{item.cantidad}</span>
                <button onClick={() => cambiarCantidad(idx, 1)} className="w-7 h-7 rounded-full bg-torta-brown text-white flex items-center justify-center" data-testid={`vendor-item-plus-${item.producto_nombre}`}>
                  <Plus className="w-3 h-3" />
                </button>
                <button onClick={() => setItemsEditados((prev) => prev.filter((_, i) => i !== idx))} className="w-7 h-7 rounded-full text-red-500 flex items-center justify-center hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}

            <select
              value=""
              onChange={(e) => e.target.value && agregarProductoEdit(e.target.value)}
              className="w-full h-9 rounded-lg border text-sm px-2"
              data-testid="vendor-add-product-select"
            >
              <option value="">+ Agregar producto...</option>
              {productosDisponibles.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre} — {formatCurrency(p.precio)}</option>
              ))}
            </select>

            <div className="flex justify-between font-bold border-t pt-2">
              <span>Nuevo Total</span>
              <span className="text-torta-brown" data-testid="vendor-nuevo-total">
                {formatCurrency(
                  itemsEditados.reduce((s, i) => s + i.cantidad * i.precio_unitario, 0) +
                    (editItemsDialog.pedido?.costo_domicilio || 0)
                )}
              </span>
            </div>

            <Textarea
              placeholder="Motivo del cambio"
              value={motivoCambio}
              onChange={(e) => setMotivoCambio(e.target.value)}
              className="text-sm h-14 resize-none"
              data-testid="vendor-edit-items-reason"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEditItemsDialog({ open: false, pedido: null })}>
                Cancelar
              </Button>
              <Button
                onClick={guardarEdicionItems}
                disabled={guardandoItems || itemsEditados.length === 0}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="vendor-save-edit-items-btn"
              >
                {guardandoItems ? "Guardando..." : "Guardar cambios"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
